import { useCallback, useEffect, useState } from 'react';
import type { AudioTrackItem } from '@components/video-player/helpers.ts';
import { findPreferredAudioTrack, getAudioTrackKey, getLanguageDisplayName } from '@components/video-player/helpers.ts';
import { getVideoAudioLanguage, persistVideoAudioLanguage } from '@store/services/localStorage.service.ts';

// Minimal AudioTrack / AudioTrackList shape (HTML spec). The TypeScript DOM lib does not include them.
// For .m3u8 URLs ReactPlayer renders <hls-video>, which fills audioTracks from the HLS audio group
// and switches hls.js when `enabled` changes (media-tracks polyfill).
interface MediaAudioTrack {
    readonly id: string;
    readonly label: string;
    readonly language: string;
    enabled: boolean;
}

interface MediaAudioTrackList extends EventTarget, Iterable<MediaAudioTrack> {
    readonly length: number;
}

type MediaElementWithAudioTracks = HTMLVideoElement & { audioTracks?: MediaAudioTrackList };

const getAudioTrackList = (mediaElement: HTMLVideoElement | null): MediaAudioTrackList | undefined => {
    const trackList = (mediaElement as MediaElementWithAudioTracks | null)?.audioTracks;

    // some environments expose a stub without events (e.g. jsdom: a plain array); then there is nothing to switch
    return typeof trackList?.addEventListener === 'function' ? trackList : undefined;
};

const enableOnlyAudioTrack = (trackList: MediaAudioTrackList, trackId: string) => {
    for (const track of trackList) {
        track.enabled = track.id === trackId;
    }
};

const toAudioTrackItem = ({ id, label, language }: MediaAudioTrack): AudioTrackItem => ({ id, label, language });

// current stream tracks and the enabled one, for React state (the browser changes the list, React does not see it)
const getAudioTrackState = (trackList: MediaAudioTrackList) => {
    const tracks = Array.from(trackList);

    return {
        tracks: tracks.map(toAudioTrackItem),
        // no DEFAULT=YES in the playlist: show the first track, the same rule as before Play
        selectedId: tracks.find((track) => track.enabled)?.id ?? tracks[0]?.id ?? null,
    };
};

// enables the first track that matches the keys (saved pick, then app locale); no match keeps DEFAULT=YES
const setPreferredAudioTrack = (trackList: MediaAudioTrackList, keys: (string | null)[]) => {
    const preferredTrack = findPreferredAudioTrack(Array.from(trackList), keys);

    if (preferredTrack && !preferredTrack.enabled) {
        enableOnlyAudioTrack(trackList, preferredTrack.id);
    }
};

// API language code -> menu item, used until the stream is loaded (ids never match stream track ids)
const toApiAudioTrackItem = (language: string): AudioTrackItem => ({
    id: `api-${language}`,
    label: getLanguageDisplayName(language),
    language,
});

const NO_AUDIO_TRACKS: AudioTrackItem[] = [];

/**
 * Audio languages of the current video (HLS alternate audio).
 * Before the stream is loaded (no Play yet), the list comes from the API (`apiLanguages`, language codes).
 * Start selection: the language the user picked (saved in the browser), then the UI language,
 * else the stream default (before Play: the first API language).
 * The choice is applied again when the stream reloads (e.g. the temporary URL is refreshed).
 */
export const useAudioTracks = (
    mediaElement: HTMLVideoElement | null,
    uiLocale: string,
    apiLanguages?: string[] | null,
) => {
    const [streamTracks, setStreamTracks] = useState<AudioTrackItem[]>([]);
    const [selectedStreamTrackId, setSelectedStreamTrackId] = useState<string | null>(null);
    // the user's choice (language code); starts with the language saved in the browser
    // lazy initial state: localStorage is read once on mount, not on every render (the player re-renders every second)
    const [chosenKey, setChosenKey] = useState<string | null>(() => getVideoAudioLanguage());

    const updateStreamTrackState = useCallback(() => {
        const trackList = getAudioTrackList(mediaElement);

        if (!trackList) {
            return;
        }

        const { tracks, selectedId } = getAudioTrackState(trackList);

        setStreamTracks(tracks);
        setSelectedStreamTrackId(selectedId);
    }, [mediaElement]);

    // only when tracks are added/removed (new manifest) or the choice changes, so a manual switch is not overridden
    const handleTracksChanged = useCallback(() => {
        const trackList = getAudioTrackList(mediaElement);

        if (!trackList) {
            return;
        }

        setPreferredAudioTrack(trackList, [chosenKey, uiLocale]);
        updateStreamTrackState();
    }, [mediaElement, chosenKey, uiLocale, updateStreamTrackState]);

    // the same function objects are added and removed; a new choice or locale re-subscribes and runs the check again
    useEffect(() => {
        const trackList = getAudioTrackList(mediaElement);

        if (!trackList) {
            return undefined;
        }

        // tracks can already be in the list before we subscribe, so read them once now
        // eslint-disable-next-line react-hooks/set-state-in-effect -- sync with an external list, as in a subscription
        handleTracksChanged();
        trackList.addEventListener('addtrack', handleTracksChanged);
        trackList.addEventListener('removetrack', handleTracksChanged);
        trackList.addEventListener('change', updateStreamTrackState);

        return () => {
            trackList.removeEventListener('addtrack', handleTracksChanged);
            trackList.removeEventListener('removetrack', handleTracksChanged);
            trackList.removeEventListener('change', updateStreamTrackState);
        };
    }, [mediaElement, handleTracksChanged, updateStreamTrackState]);

    const selectAudioTrack = useCallback(
        (track: AudioTrackItem) => {
            const key = getAudioTrackKey(track);
            setChosenKey(key);
            persistVideoAudioLanguage(key);

            const trackList = getAudioTrackList(mediaElement);

            // before Play there is no stream track yet; the effect applies the choice when the tracks arrive
            if (trackList && Array.from(trackList).some((item) => item.id === track.id)) {
                enableOnlyAudioTrack(trackList, track.id);
            }
        },
        [mediaElement],
    );

    const trackList = getAudioTrackList(mediaElement);

    // stream loaded: real tracks from the HLS playlist
    if (trackList && streamTracks.length) {
        return { audioTracks: streamTracks, selectedAudioTrackId: selectedStreamTrackId, selectAudioTrack };
    }

    // player has no track list (YouTube, MP4 in Chrome): nothing to choose
    if (mediaElement && !trackList) {
        return { audioTracks: NO_AUDIO_TRACKS, selectedAudioTrackId: null, selectAudioTrack };
    }

    // not played yet or playlist still loading: languages from the API
    const apiTracks = [...new Set(apiLanguages?.filter(Boolean))].map(toApiAudioTrackItem);
    const preselectedTrack = findPreferredAudioTrack(apiTracks, [chosenKey, uiLocale]) ?? apiTracks[0];

    return { audioTracks: apiTracks, selectedAudioTrackId: preselectedTrack?.id ?? null, selectAudioTrack };
};
